import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedConnectionRuntimeConfig } from "../services/connectionRuntimeConfig";

interface IUseFetchConnectionRuntimeConfig {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: any;
  pSize: any;
  filterId?: string | number;
  isActive?: string;
  code?: string;
  connection_type?: string;
}

const useFetchPaginatedConnectionRuntimeConfig = ({
  setIsLoading,
  page,
  pSize,
  filterId,
  isActive = "true",
  code,
  connection_type,
}: IUseFetchConnectionRuntimeConfig) => {
  const [connectionRuntimeConfig, setConnectionRuntimeConfig] = useState<any>(
    []
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (filterIdParam?: string | number) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedConnectionRuntimeConfig({
          page,
          pSize,
          filterId: filterIdParam || filterId,
          isActive,
          code,
          connection_type,
          signal: abortControllerRef.current.signal,
        });
        setConnectionRuntimeConfig(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setConnectionRuntimeConfig([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, filterId, isActive, code, connection_type, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [connectionRuntimeConfig, fetchData];
};

export default useFetchPaginatedConnectionRuntimeConfig;
